/**
 * Creation-order playback.
 *
 * Playback is LINEAR IN CREATION RANK, not in wall-clock time. Real creation
 * timestamps cluster hard (an import drops hundreds of notes in one second)
 * and leave long empty gaps, so playing in real time means staring at nothing
 * and then missing a blur. Walking the creation-ordered index linearly over a
 * fixed duration handles both automatically, and the scrubber still labels the
 * cursor with the real date at that rank.
 *
 * Because build.ts emits nodes sorted by entranceAt and links sorted by their
 * later endpoint, "what exists now" is a prefix of each array, so advancing is
 * a counter rather than a per-frame membership test.
 */
import type { GraphData } from "./types";

export type PlaybackState = "idle" | "playing" | "paused" | "finished";

const MIN_DURATION_MS = 6000;
const MAX_DURATION_MS = 30000;
const MS_PER_NODE = 22;

export function playbackDuration(nodeCount: number): number {
  return Math.min(MAX_DURATION_MS, Math.max(MIN_DURATION_MS, nodeCount * MS_PER_NODE));
}

export class Playback {
  state: PlaybackState = "idle";
  /** Fractional node index the playhead sits at. */
  cursor = 0;
  private total = 0;
  private duration = MIN_DURATION_MS;
  private lastTick = 0;
  /** Wall-clock ms at which each node became live, for the entrance ease. */
  private activatedAt = new Float64Array(0);
  private linkMax = new Int32Array(0);
  private enterMs = 420;

  /** Rebind to a topology. Keeps the playhead if one is running. */
  setGraph(data: GraphData, enterMs: number): void {
    this.total = data.nodes.length;
    this.duration = playbackDuration(this.total);
    this.enterMs = enterMs;
    const prev = this.activatedAt;
    this.activatedAt = new Float64Array(this.total);
    // carry over activation stamps so a rebuild mid-playback does not re-pop
    // every node that was already on screen
    const carry = Math.min(prev.length, this.total);
    for (let i = 0; i < carry; i++) this.activatedAt[i] = prev[i]!;
    this.linkMax = new Int32Array(data.links.length);
    for (let j = 0; j < data.links.length; j++) this.linkMax[j] = data.links[j]!.maxIndex;
    if (this.state === "idle") this.cursor = this.total;
    else this.cursor = Math.min(this.cursor, this.total);
  }

  get totalNodes(): number {
    return this.total;
  }

  get isAnimating(): boolean {
    return this.state !== "idle";
  }

  /** 0..1 for the scrubber. */
  get progress(): number {
    return this.total ? Math.min(1, this.cursor / this.total) : 0;
  }

  play(now: number): void {
    if (this.state === "idle" || this.state === "finished") {
      this.cursor = 0;
      this.activatedAt.fill(0);
    }
    this.state = "playing";
    this.lastTick = now;
  }

  pause(): void {
    if (this.state === "playing") this.state = "paused";
  }

  toggle(now: number): void {
    if (this.state === "playing") this.pause();
    else this.play(now);
  }

  /** Back to the full graph, everything fully entered. */
  reset(): void {
    this.state = "idle";
    this.cursor = this.total;
    this.activatedAt.fill(0);
  }

  seek(fraction: number, now: number): void {
    if (this.state === "idle") this.state = "paused";
    const next = Math.max(0, Math.min(1, fraction)) * this.total;
    // everything behind the playhead is fully entered, so scrubbing back and
    // forth does not leave half-faded nodes behind
    for (let i = 0; i < this.total; i++) {
      this.activatedAt[i] = i < next ? now - this.enterMs : 0;
    }
    this.cursor = next;
    this.lastTick = now;
    if (next >= this.total) this.state = "finished";
  }

  /** Advance the playhead. Returns the live node count. */
  advance(now: number): number {
    if (this.state === "idle") return this.total;
    if (this.state === "playing") {
      const dt = Math.min(100, now - this.lastTick);
      this.lastTick = now;
      const perMs = this.total / this.duration;
      const before = Math.floor(this.cursor);
      this.cursor = Math.min(this.total, this.cursor + dt * perMs);
      const after = Math.floor(this.cursor);
      for (let i = before; i < after; i++) if (!this.activatedAt[i]) this.activatedAt[i] = now;
      if (this.cursor >= this.total) this.state = "finished";
    }
    return Math.floor(this.cursor);
  }

  /** Live links: a prefix, since links are ordered by their later endpoint. */
  activeLinks(activeNodes: number): number {
    let lo = 0;
    let hi = this.linkMax.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.linkMax[mid]! < activeNodes) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  /** Fill 0..1 entrance progress per node for the renderer. */
  fillEnter(out: Float32Array, activeNodes: number, now: number): void {
    if (this.state === "idle") {
      out.fill(1);
      return;
    }
    for (let i = 0; i < out.length; i++) {
      if (i >= activeNodes) {
        out[i] = 0;
        continue;
      }
      const at = this.activatedAt[i]!;
      out[i] = at ? Math.min(1, (now - at) / this.enterMs) : 1;
    }
  }

  /** Real creation date at the playhead, for the scrubber label. */
  dateAt(data: GraphData): number | null {
    if (!this.total) return null;
    const i = Math.max(0, Math.min(this.total - 1, Math.floor(this.cursor)));
    return data.nodes[i]?.entranceAt ?? null;
  }
}
